import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal
} from '@/components/ui/dialog';
import { useGenerateBlog } from '@app/Hooks/use-blogs';

const MIN_DESC = 10;
const MAX_DESC = 1000;

const validationSchema = Yup.object({
  description: Yup.string()
    .min(MIN_DESC, `Description must be at least ${MIN_DESC} characters`)
    .max(MAX_DESC, `Description must be at most ${MAX_DESC} characters`)
    .required('Description is required')
});

const GenerateBlogModal = ({ isOpen, onOpenChange }) => {
  const { mutateAsync, isPending } = useGenerateBlog();
  const formik = useFormik({
    initialValues: {
      description: ''
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await mutateAsync(values);
      resetForm();
      onOpenChange?.(false);
    }
  });

  const charCount = formik.values.description.length;
  const isOverLimit = charCount > MAX_DESC;
  const isTouched = formik.touched.description;
  const hasError = isTouched && Boolean(formik.errors.description);

  const handleOpenChange = (next) => {
    if (!next) {
      formik.resetForm();
    }
    onOpenChange?.(next);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogContent
          className="
            fixed left-1/2 top-1/2 z-50
            w-full !max-w-2xl -translate-x-1/2 -translate-y-1/2
            rounded-2xl  bg-white p-0 !border-0
            focus:outline-none
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]
            data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]
          "
        >
          <div className="px-6 py-5 border-b border-gray-300">
            <div className="flex gap-3 items-center">
              <div>
                <h2 className="font-semibold tracking-tight text-black font-outfit">
                  Generate Blog Post
                </h2>
                <p className="mt-0.5 text-xs text-zinc-500">
                  Describe what you'd like to write about
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={formik.handleSubmit} noValidate>
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-black"
                >
                  Description
                  <span className="ml-1 text-red-400">*</span>
                </label>

                <div className="relative">
                  <textarea
                    id="description"
                    name="description"
                    rows={5}
                    placeholder="e.g. A guide to the health benefits of West African superfoods like moringa, tiger nuts, and baobab — and how to easily add them to your daily meals..."
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`
                      w-full resize-none rounded-xl border bg-gray-100 px-4 py-3
                      text-sm text-black placeholder-zinc-600
                      transition-colors duration-150
                      focus:outline-none focus:ring-2
                      ${
                        hasError
                          ? 'border-red-500/60 focus:ring-red-500/30'
                          : isOverLimit
                          ? 'border-amber-500/60 focus:ring-amber-500/30'
                          : 'border-zinc-800 focus:border-[#003232]/50 focus:ring-[#003232]/20'
                      }
                    `}
                  />

                  {/* Char counter badge */}
                  <span
                    className={`
                      absolute bottom-3 right-3 rounded-md px-1.5 py-0.5
                      text-[11px] font-mono tabular-nums
                      ${
                        isOverLimit
                          ? 'bg-red-500/15 text-red-400'
                          : charCount >= MAX_DESC * 0.9
                          ? 'bg-amber-500/15 text-amber-400'
                          : 'bg-zinc-800 text-zinc-500'
                      }
                    `}
                  >
                    {charCount}/{MAX_DESC}
                  </span>
                </div>

                {/* Error / hint */}
                <div className="min-h-[18px]">
                  {hasError ? (
                    <p className="flex gap-1.5 items-center text-xs text-red-400">
                      <svg
                        className="w-3.5 h-3.5 shrink-0"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.75 3.5v4a.75.75 0 0 1-1.5 0v-4a.75.75 0 0 1 1.5 0zm-.75 6.25a.875.875 0 1 1 0 1.75.875.875 0 0 1 0-1.75z" />
                      </svg>
                      {formik.errors.description}
                    </p>
                  ) : (
                    <p className="text-xs text-zinc-600">
                      Between {MIN_DESC} and {MAX_DESC} characters
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 justify-end items-center px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                className="px-4 py-2 text-sm font-medium bg-transparent rounded-lg border transition-colors border-[#003232] text-zinc-400"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={
                  isPending ||
                  isOverLimit ||
                  (!formik.isValid && formik.submitCount > 0)
                }
                className="
                  relative inline-flex items-center gap-2 rounded-lg
                  bg-[#003232] px-4 py-2 text-sm font-medium text-white
                  transition-all duration-150
                  hover:[#003232] active:scale-[0.98]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950
                  disabled:cursor-not-allowed disabled:opacity-40
                "
              >
                {isPending ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Generating…
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                      />
                    </svg>
                    Generate Post
                  </>
                )}
              </button>
            </div>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export default GenerateBlogModal;
